import { useMemo } from 'react'
import { Clock, Wrench, MessageSquare, Activity } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Call, CallEvent } from '../../api/telemetry'
import { StatusBadge } from '../shared/StatusBadge'
import { cn, formatDuration, formatTimestamp } from '../../lib/utils'

interface MetricCardProps {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
}

function MetricCard({ icon, label, value }: MetricCardProps) {
  return (
    <div className="flex items-center gap-3 rounded-lg bg-surface p-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </div>
      <div>
        <p className="text-xs text-text-muted">{label}</p>
        <p className="text-sm font-semibold text-text-primary">{value}</p>
      </div>
    </div>
  )
}

const eventDotColors: Record<string, string> = {
  'call.started': 'bg-success',
  'call.ended': 'bg-text-muted',
  'call.tool.invoked': 'bg-purple-400',
  'call.tool.result': 'bg-purple-400',
  'call.error': 'bg-error',
  'call.transcript.user': 'bg-info',
  'call.transcript.bot': 'bg-primary',
}

function getEventDotColor(eventType: string): string {
  return eventDotColors[eventType] ?? 'bg-text-muted'
}

export function CallMetrics({ call, events }: { call: Call; events: CallEvent[] }) {
  const { t } = useTranslation('calls')
  const toolInvocations = useMemo(
    () => events.filter((e) => e.event_type === 'call.tool.invoked').length,
    [events]
  )

  const transcriptTurns = useMemo(
    () => events.filter((e) => e.event_type.includes('transcript')).length,
    [events]
  )

  const timelineEvents = useMemo(() => {
    if (!call.duration_s || call.duration_s <= 0) return []
    return [...events]
      .sort((a, b) => a.timestamp - b.timestamp)
      .map((e) => ({
        ...e,
        position: Math.min(Math.max(((e.timestamp - call.started_at) / call.duration_s!) * 100, 0), 100),
      }))
  }, [events, call.started_at, call.duration_s])

  return (
    <div className="rounded-xl border border-border bg-surface-elevated p-6">
      <h2 className="mb-4 text-lg font-semibold text-text-primary">{t('detail.metrics.title')}</h2>

      {/* Stats grid */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <MetricCard
          icon={<Clock className="h-4 w-4" />}
          label={t('detail.metrics.duration')}
          value={formatDuration(call.duration_s)}
        />
        <MetricCard
          icon={<Wrench className="h-4 w-4" />}
          label={t('detail.metrics.toolInvocations')}
          value={toolInvocations}
        />
        <MetricCard
          icon={<MessageSquare className="h-4 w-4" />}
          label={t('detail.metrics.transcriptTurns')}
          value={transcriptTurns}
        />
        <MetricCard
          icon={<Activity className="h-4 w-4" />}
          label={t('detail.metrics.status')}
          value={<StatusBadge status={call.status} />}
        />
      </div>

      {/* Timeline */}
      {call.duration_s && call.duration_s > 0 && (
        <div>
          <h3 className="mb-3 text-sm font-medium text-text-secondary">{t('detail.metrics.timeline')}</h3>
          <div className="relative">
            {/* Track */}
            <div className="h-2 w-full rounded-full bg-surface" />

            {/* Filled track */}
            <div
              className="absolute top-0 left-0 h-2 rounded-full bg-primary/30"
              style={{ width: '100%' }}
            />

            {/* Event dots */}
            {timelineEvents.map((event, i) => (
              <div
                key={`${event.id}-${i}`}
                className="group absolute top-1/2 -translate-y-1/2"
                style={{ left: `${event.position}%` }}
              >
                <div
                  className={cn(
                    'h-3 w-3 -ml-1.5 rounded-full border-2 border-surface-elevated cursor-pointer transition-transform hover:scale-150',
                    getEventDotColor(event.event_type)
                  )}
                />
                {/* Tooltip */}
                <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                  <div className="whitespace-nowrap rounded-lg bg-surface-card px-3 py-1.5 text-xs shadow-lg border border-border">
                    <p className="font-medium text-text-primary">{event.event_type}</p>
                    <p className="text-text-muted">{formatTimestamp(event.timestamp)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Timeline labels */}
          <div className="mt-2 flex justify-between text-[10px] text-text-muted">
            <span>{formatTimestamp(call.started_at)}</span>
            <span>{call.ended_at ? formatTimestamp(call.ended_at) : t('detail.metrics.ongoing')}</span>
          </div>
        </div>
      )}
    </div>
  )
}
